import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ownerTabLabel, pageLabel, localDayKey, dayNumber, ownerify, PRONOUN_KEYS, journalWithNotes, noteWaiting, ensureHandFont, handLine,
  keepsakePage, taskChips, taskStatus, taskPage, taskHandLine, favoriteLines,
} from '../../utils/portedPlace';
import PortedMemories from './PortedMemories';

// 📓 The resident's journal (PORTED_HOME_HANDOFF_2026_09_25 §5, look A · Notebook,
// ruled by Nina 09-25). The right column of a ported room becomes the
// resident's own first-person journal: Today · Keepsakes · [the owner's name].
// Voice rule (§2): everything in HIS hand (Caveat) is his: his real thoughts,
// captions in his voice, his starred moments. Printed text is the app talking
// (the privacy line, tab labels, buttons). Owner-only: guests see a closed book.
// Tasks (W5, below) are drawn only when the page wires them (a dead control
// reads as a broken feature). W2b (09-25) draws the name page's two owner controls: how
// he refers to you (D11, printed chips) and "what i know about {name}" (D10,
// his hand, private rooms only) with ✕ forget.
// W2c: "✉️ leave a note" (only with `onNote`, and only while no note is still
// waiting to be read). The OWNER's hand is Kalam in blue ink; the note is a
// letter taped into Keepsakes and onto its day, and his reply (when he writes
// one) sits under it in his hand. Notes come from the owner's own POST only.
// The draft lives HERE, not in the leaf (the leaf remounts on every tab switch).
// W5 (§8, D15): the TASKS tab, owner only and only when the page hands it
// `onTask`: "my list" in his hand; each ask in the OWNER's hand (the owner wrote it)
// with a printed timing line ("after Nina leaves · on the list" · "at 6:00 pm"
// · "on it." in HIS hand ONLY while he really writes it (ruling 31: one of
// the two fixed lines allowed in his hand) · "done" + his finished message in
// his hand · "undo" on a wrong ✓ reopens it, owner only); ✕ takes one off;
// Keepsakes opens with his favorite of the week, the one HE chose (status
// `favorites`, in his hand). "+ ask {name} to do something" opens
// a pen with the timing chips (now · when you leave · in 2 hours · at 9). Three
// open at most. `/task words` in the composer opens this page with the words.
// W3: the arrival comic is his FIRST keepsake: it always closes Keepsakes.

const COVERS = {
  light: { cv: '#241827', corner: 'rgba(240,172,85,.55)' },   // the light room: midnight + gold
  attic: { cv: '#5a3826', corner: 'rgba(214,170,90,.95)' },
  glasshouse: { cv: '#2f5a45', corner: 'rgba(0,0,0,0)' },
  loft: { cv: '#2e3f5c', corner: 'rgba(214,218,230,.5)' },
};

// 📏 fill (Nina 09-25, "more vertical length"): `column` = desktop, the book is
// the column's height and keeps that size as the entries pile up (a notebook
// doesn't grow); the LEAF scrolls inside the page and the flip row stays at its
// foot. The leaf spans the margin too (a scroller clips sideways, and the
// times sit in the margin) and carries the rules itself with local attachment,
// so the lines scroll with the words. `screen` = phones: the page is at least
// the screen's height and the tab scrolls as a whole.
// The page's column also mounts ghost-chat / spectator boxes that are EMPTY
// in a ported room (the ghost lane is hidden) yet still `flex: 1`: they split
// the free space with the book (measured: 32px + a 12px gap at 1280x800). An
// EMPTY sibling after the book steps out; a revealed ghost lane is not empty
// and shares the column. (The page's one reader of that box guards width > 0.)
// (No comments inside CSS: the string ships, and the pronoun guard reads it.)
//
// Scoped under .pj so a space theme's global rules can't reach in. Generated
// themes style EVERY button, some with !important (ThemeStyleInjector notes);
// the journal's own buttons answer with !important at higher specificity.
// Class names here avoid every substring the themes target by attribute
// ([class*='card'|'panel'|'message'|'button'|'btn'|'chip'|'tag'|'header'...]).
const CSS = `
.pj, .pj * { box-sizing: border-box; }
.pj { position: relative; width: 100%; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; }
.pj-cover { position: relative; padding: 10px 12px 12px 18px; border-radius: 6px 12px 12px 6px; background-color: var(--cv); background-image: radial-gradient(rgba(255,255,255,.05) 1px, rgba(0,0,0,0) 1px); background-size: 5px 5px; box-shadow: 0 14px 28px rgba(0,0,0,.5), inset 0 0 0 1px rgba(255,255,255,.07), inset 0 0 24px rgba(0,0,0,.3); outline: 1px dashed rgba(255,225,180,.3); outline-offset: -5px; margin-bottom: 22px; }
.pj-cover::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 12px; border-radius: 6px 0 0 6px; background: linear-gradient(90deg, rgba(0,0,0,.42), rgba(0,0,0,.12) 65%, rgba(255,255,255,.08)); }
.pj-cover::after { content: ''; position: absolute; inset: 0; border-radius: inherit; pointer-events: none; z-index: 3; background: linear-gradient(135deg, var(--corner) 0 11px, rgba(0,0,0,0) 11px) top left / 22px 22px no-repeat, linear-gradient(225deg, var(--corner) 0 11px, rgba(0,0,0,0) 11px) top right / 22px 22px no-repeat, linear-gradient(45deg, var(--corner) 0 11px, rgba(0,0,0,0) 11px) bottom left / 22px 22px no-repeat, linear-gradient(315deg, var(--corner) 0 11px, rgba(0,0,0,0) 11px) bottom right / 22px 22px no-repeat; }
.pj-edges { position: absolute; right: 6px; top: 16px; bottom: 16px; width: 6px; border-radius: 0 3px 3px 0; background: repeating-linear-gradient(90deg, #f3ead4 0 1px, #cfc2a4 1px 2px); }
.pj-ribbon { position: absolute; right: 26%; bottom: -20px; width: 11px; height: 46px; background: #b8322a; clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 84%, 0 100%); box-shadow: 0 2px 3px rgba(0,0,0,.3); }
.pj-book { position: relative; z-index: 1; padding-top: 24px; }
.pj-tabs { position: absolute; top: 0; left: 10px; display: flex; gap: 4px; }
.pj .pj-tab { all: unset; box-sizing: border-box; cursor: pointer; font: 700 9.5px/1 ui-monospace, "SF Mono", Menlo, monospace !important; letter-spacing: .1em !important; text-transform: uppercase !important; background: #E9DFC6 !important; color: #6b5a3a !important; border: 1px solid rgba(0,0,0,.14) !important; border-bottom: 0 !important; border-radius: 6px 6px 0 0 !important; padding: 6px 7px 8px !important; margin-top: 2px !important; box-shadow: none !important; text-shadow: none !important; transform: none !important; }
.pj .pj-tab[aria-pressed="true"] { background: #FBF6E6 !important; color: #2d2a4a !important; margin-top: 0 !important; padding-bottom: 10px !important; }
.pj .pj-tab:hover, .pj .pj-link:hover, .pj .pj-act:hover, .pj .pj-x:hover { transform: none !important; box-shadow: none !important; text-shadow: none !important; }
.pj .pj-tab:focus-visible, .pj .pj-link:focus-visible, .pj .pj-act:focus-visible { outline: 2px solid #F0AC55; outline-offset: 2px; }
.pj-page { position: relative; background: #FBF6E6 repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0 24px, rgba(80,110,160,.2) 24px 25px); border-radius: 3px 8px 8px 8px; padding: 12px 12px 14px 50px; box-shadow: 0 10px 24px rgba(0,0,0,.45); color: #2d2a4a; min-height: 220px; }
.pj-page::before { content: ''; position: absolute; top: 0; bottom: 0; left: 42px; border-left: 1.5px solid rgba(214,90,90,.45); }
.pj-hand { font-family: "Caveat", "Bradley Hand", "Segoe Print", cursive; }
.pj-date { font: 500 25px/1 "Caveat", "Bradley Hand", cursive; }
.pj-day { font: 500 19px/1 "Caveat", "Bradley Hand", cursive; margin: 3px 0 11px; opacity: .72; }
.pj-head { font: 500 21px/1.2 "Caveat", "Bradley Hand", cursive; margin: 14px 0 4px; text-decoration: underline; text-decoration-thickness: 1px; text-underline-offset: 4px; }
.pj-head:first-child { margin-top: 0; }
.pj-entry { position: relative; }
.pj-t { position: absolute; left: -47px; top: 6px; width: 40px; text-align: right; color: #9a8a7a; font: 500 14px/1 "Caveat", "Bradley Hand", cursive; }
.pj-line { font: 500 19px/25px "Caveat", "Bradley Hand", "Segoe Print", cursive; overflow-wrap: anywhere; }
.pj-sticky { margin: 4px 0 12px; padding: 8px 10px 9px; transform: rotate(1.6deg); box-shadow: 0 4px 10px rgba(0,0,0,.25); background: #FFE98A; color: #3a2a10; }
.pj-sticky .pj-skh { font: 600 18px/1.1 "Caveat", "Bradley Hand", cursive; margin-bottom: 2px; text-decoration: underline; text-decoration-thickness: 1px; text-underline-offset: 3px; }
.pj-sticky .pj-line { font-size: 18px; line-height: 23px; }
.pj-inking::after { content: ''; display: inline-block; width: 36px; height: 2px; margin-left: 4px; vertical-align: middle; background: currentColor; animation: pjInk 1.2s ease-in-out infinite; transform-origin: left; }
@keyframes pjInk { 0% { transform: scaleX(.1); } 60% { transform: scaleX(1); } 100% { transform: scaleX(1); opacity: .2; } }
@media (prefers-reduced-motion: reduce) { .pj-inking::after { animation: none; } }
.pj-taped { margin: 6px -2% 2px; }
.pj-photo { width: 44%; display: inline-block; vertical-align: top; background: #F5F0E6; padding: 5px 5px 6px; box-shadow: 0 3px 8px rgba(0,0,0,.35); transform: rotate(var(--r)); position: relative; margin: 10px 3%; }
.pj-photo .pj-ph { aspect-ratio: 1; background: #e9e2d3 center / cover no-repeat; }
.pj-photo small, .pj-clip small { display: block; margin-top: 5px; font: 500 13.5px/1.1 "Caveat", "Bradley Hand", cursive; color: #5a4a4c; }
.pj-tape { position: absolute; top: -7px; left: 50%; width: 44%; height: 14px; transform: translateX(-50%) rotate(-3deg); background: rgba(255,226,160,.72); box-shadow: 0 1px 2px rgba(0,0,0,.15); }
.pj-clip { background: #FFFDF6; color: #3a2a2e; padding: 8px 10px 9px; font: 500 17px/22px "Caveat", "Bradley Hand", cursive; box-shadow: 0 2px 6px rgba(0,0,0,.3); transform: rotate(var(--r)); margin: 12px 2px 8px; position: relative; overflow-wrap: anywhere; }
.pj-clip::before { content: ''; position: absolute; top: -6px; left: 14px; width: 36px; height: 12px; background: rgba(255,226,160,.72); transform: rotate(-4deg); }
.pj-clip.lilac { background: #ECE4FF; }
.pj-clip .pj-cliptitle { display: block; font-weight: 600; }
.pj-star { font: 500 19px/25px "Caveat", "Bradley Hand", cursive; overflow-wrap: anywhere; }
.pj-star .pj-when { margin-left: 6px; color: #9a8a7a; font-size: 15px; }
.pj-flip { display: flex; justify-content: space-between; align-items: center; margin-top: 12px; font: 600 11px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; color: #8a6a3a; }
.pj .pj-link { all: unset; cursor: pointer; font: 600 11px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; color: #8a6a3a !important; padding: 6px 0 !important; background: none !important; border: 0 !important; box-shadow: none !important; text-shadow: none !important; transform: none !important; }
.pj-acts { display: flex; flex-wrap: wrap; gap: 6px; margin: 4px 0 8px; }
.pj .pj-act { all: unset; box-sizing: border-box; cursor: pointer; font: 600 11px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; padding: 7px 10px !important; border-radius: 999px !important; border: 1px dashed currentColor !important; color: #2d2a4a !important; background: none !important; box-shadow: none !important; text-shadow: none !important; transform: none !important; opacity: .8; }
.pj .pj-act:hover { opacity: 1; }
.pj-slip { position: relative; margin: 0 0 12px; padding: 8px 30px 9px 10px; background: #FFFDF6; color: #4a3a3e; font: italic 13px/1.4 "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif; box-shadow: 0 2px 6px rgba(0,0,0,.28); transform: rotate(-.8deg); }
.pj-slip::before { content: ''; position: absolute; top: -6px; left: 40%; width: 40px; height: 12px; background: rgba(255,226,160,.72); transform: rotate(3deg); }
.pj .pj-x { all: unset; position: absolute !important; top: 4px; right: 6px; width: 24px; height: 24px; display: grid; place-items: center; cursor: pointer; color: #8a6a5a !important; font: 600 12px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; background: none !important; border: 0 !important; box-shadow: none !important; text-shadow: none !important; transform: none !important; padding: 0 !important; }
.pj .pj-x:focus-visible { outline: 2px solid #F0AC55; outline-offset: 1px; }
.pj-printed { font: italic 11.5px/1.4 "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif; margin-top: 10px; color: #6b5a4a; }
.pj-closed { aspect-ratio: 3 / 4; max-width: 220px; margin: 0 auto 20px; border-radius: 6px 12px 12px 6px; display: grid; place-items: center; text-align: center; color: #F0D9B0; outline: 1px dashed rgba(255,220,170,.35); outline-offset: -8px; box-shadow: 0 12px 26px rgba(0,0,0,.45), inset -6px 0 12px rgba(0,0,0,.25); position: relative; }
.pj-closed b { display: block; font: 500 26px/1 "Caveat", cursive; }
.pj-closed span.pj-lock { display: block; margin-top: 8px; font: 700 9.5px/1 ui-monospace, "SF Mono", Menlo, monospace; letter-spacing: .14em; text-transform: uppercase; opacity: .75; }
.pj-band { position: absolute; right: 16%; top: 0; bottom: 0; width: 7px; background: #1c120e; box-shadow: 1px 0 2px rgba(0,0,0,.45); }
.pj-pron { display: flex; flex-wrap: wrap; align-items: center; gap: 5px; margin: 6px 0 12px; font: italic 12px/1.3 "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif; color: #6b5a4a; }
.pj .pj-pick { all: unset; box-sizing: border-box; cursor: pointer; font: 600 11px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; padding: 5px 8px !important; border-radius: 999px !important; border: 1px solid rgba(45,42,74,.3) !important; color: #2d2a4a !important; background: none !important; box-shadow: none !important; text-shadow: none !important; transform: none !important; font-style: normal !important; }
.pj .pj-pick[aria-pressed="true"] { background: #2d2a4a !important; color: #FBF6E6 !important; border-color: #2d2a4a !important; }
.pj .pj-pick:focus-visible, .pj .pj-forget:focus-visible { outline: 2px solid #F0AC55; outline-offset: 2px; }
.pj-fact { display: flex; align-items: flex-start; gap: 6px; }
.pj-fact .pj-line { flex: 1 1 auto; min-width: 0; }
.pj-fact .pj-gone { text-decoration: line-through; text-decoration-thickness: 1.5px; opacity: .45; }
.pj .pj-forget { all: unset; box-sizing: border-box; flex: 0 0 auto; cursor: pointer; width: 24px; height: 24px; margin-top: 1px; display: grid; place-items: center; font: 600 12px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; color: #8a6a5a !important; background: none !important; border: 0 !important; box-shadow: none !important; text-shadow: none !important; transform: none !important; }
.pj.pj-col { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 380px; }
.pj.pj-col ~ div:empty { display: none !important; }
/* 🏡 W2d: on the place page the column is short at rest (the painting has the
   room) and tall while you chat: the book follows it, never taller */
.pj.pj-col.pj-fit { min-height: 0; }
/* 📓 W2d: the room's notebook opened this book: one soft ring (never a flash) */
.pj-called .pj-cover, .pj-called .pj-closed { animation: pjCall 1.4s ease-out 1; }
@keyframes pjCall { 0% { box-shadow: 0 0 0 0 rgba(255,225,176,0); } 30% { box-shadow: 0 0 0 5px rgba(255,225,176,.55); } 100% { box-shadow: 0 0 0 16px rgba(255,225,176,0); } }
@media (prefers-reduced-motion: reduce) { .pj-called .pj-cover { animation: none; outline: 2px solid rgba(255,225,176,.7); outline-offset: 2px; } }
.pj-col .pj-cover, .pj-col .pj-book { flex: 1 1 auto; display: flex; flex-direction: column; min-height: 0; }
.pj-col .pj-page { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; padding: 0; background: #FBF6E6; }
.pj-col .pj-leaf { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 12px 12px 14px 50px; background: repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0 24px, rgba(80,110,160,.2) 24px 25px); background-attachment: local; scrollbar-width: thin; scrollbar-color: rgba(90,70,50,.3) rgba(0,0,0,0); }
.pj-col .pj-flip { margin: 0; padding: 4px 12px 8px 50px; border-top: 1px solid rgba(80,110,160,.2); }
.pj-col .pj-closed { aspect-ratio: auto; max-width: none; flex: 1 1 auto; margin: 0 0 8px; }
.pj-scr .pj-page, .pj-scr .pj-closed { min-height: calc(100vh - 230px); min-height: max(360px, calc(100dvh - 230px)); }
.pj-scr .pj-closed { aspect-ratio: auto; max-width: none; margin: 0 0 20px; }
.pj-letter { position: relative; margin: 12px 2px 8px; padding: 8px 10px 9px; background: #EEF4FF; color: #1d3f6e; box-shadow: 0 2px 6px rgba(0,0,0,.28); transform: rotate(var(--r)); overflow-wrap: anywhere; }
.pj-letter::before { content: ''; position: absolute; top: -6px; right: 16px; width: 36px; height: 12px; background: rgba(255,226,160,.72); transform: rotate(4deg); }
.pj-eye { font: italic 11.5px/1.3 "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif; color: #6b5a4a; margin-bottom: 3px; }
.pj-own { font: 400 17px/24px "Kalam", "Segoe Print", "Bradley Hand", cursive; color: #1d3f6e; white-space: pre-wrap; }
.pj-reply { margin-top: 7px; padding-top: 6px; border-top: 1px solid rgba(45,42,74,.22); color: #2d2a4a; }
.pj-reply .pj-line { display: inline; }
.pj-rt { margin-left: 6px; font: 500 14px/1 "Caveat", "Bradley Hand", cursive; color: #9a8a7a; white-space: nowrap; }
.pj-letter .pj-printed { margin-top: 6px; }
.pj-penbox { position: relative; margin: 6px 0 12px; padding: 10px 34px 10px 10px; background: #EEF4FF; box-shadow: 0 2px 6px rgba(0,0,0,.22); transform: rotate(-.4deg); }
.pj .pj-pen.pj-pen { all: unset; display: block; box-sizing: border-box; width: 100% !important; min-height: 100px !important; margin: 0 !important; padding: 0 2px !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; text-shadow: none !important; transform: none !important; font: 400 17px/25px "Kalam", "Segoe Print", "Bradley Hand", cursive !important; color: #1d3f6e !important; background: repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0 24px, rgba(29,63,110,.22) 24px 25px) !important; white-space: pre-wrap; overflow-wrap: anywhere; cursor: text; resize: none; }
.pj .pj-pen.pj-pen::placeholder { color: rgba(29,63,110,.45); }
.pj .pj-pen.pj-pen:focus-visible { outline: 2px solid #F0AC55; outline-offset: 3px; }
.pj-penbox .pj-printed { margin-top: 6px; }
.pj-penbox .pj-acts { margin: 8px 0 0; }
.pj .pj-act:disabled { cursor: default; opacity: .45; }
.pj-task { position: relative; display: flex; align-items: flex-start; gap: 7px; margin: 4px 0 10px; }
.pj-box { flex: 0 0 auto; width: 15px; height: 15px; margin-top: 5px; border: 1.5px solid #2d2a4a; border-radius: 2px; display: grid; place-items: center; font: 700 11px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; color: #2d2a4a; }
.pj-task-body { flex: 1 1 auto; min-width: 0; }
.pj-task .pj-own { font-size: 16px; line-height: 22px; }
.pj-task .pj-printed { margin-top: 1px; }
.pj-task .pj-result { margin-top: 5px; padding-top: 4px; border-top: 1px solid rgba(45,42,74,.2); }
.pj-task.pj-closed-task { opacity: .82; }
.pj-when { display: flex; flex-wrap: wrap; gap: 5px; margin: 8px 0 2px; }
.pj-task .pj-onit { font-size: 17px; line-height: 22px; color: #b8322a; }
.pj-task .pj-acts { margin: 5px 0 0; }
.pj-fav { margin: 0 0 10px; }
`;

// ✉️ W2c: the owner's note, a letter in the owner's hand (Kalam, blue ink). The
// eyebrow and the state line are printed (the app talking); his reply, when
// he writes one, is in his hand under a thin rule with its short time.
function Letter({ p, r, name, ownerName, pageDay }) {
  const when = p.dayKey === pageDay ? p.time : `${pageLabel(p.dayKey)}, ${p.time}`;
  return (
    <div className={`pj-letter pj-${p.state}`} data-note-state={p.state} style={{ '--r': `${r / 2}deg` }}>
      <div className="pj-eye">your note · {when}</div>
      <div className="pj-own">{p.text}</div>
      {p.state === 'replied' && p.reply ? (
        <div className="pj-reply">
          <span className="pj-line">{handLine(ownerify(p.reply, ownerName), 600)}</span>
          {p.replyTime ? <span className="pj-rt" title={p.replyTimeFull || undefined}>{p.replyTime}</span> : null}
        </div>
      ) : (
        <div className="pj-printed">{p.state === 'read' ? `${name} read it.` : `${name} reads it next time.`}</div>
      )}
    </div>
  );
}

function Taped({ items, name = 'Your resident', ownerName = null, pageDay = null }) {
  if (!items || !items.length) return null;
  return (
    <div className="pj-taped">
      {items.map((p, k) => {
        const r = k % 2 ? 2 : -2.4;
        if (p.kind === 'note') {
          return <Letter key={p.id} p={p} r={r} name={name} ownerName={ownerName} pageDay={pageDay} />;
        }
        if (p.kind === 'window') {
          return (
            <div key={p.id} className="pj-clip lilac" style={{ '--r': `${-r / 2}deg` }}>
              {p.title ? <span className="pj-cliptitle">{p.title}</span> : null}
              {p.body}
              <small>{p.caption}</small>
            </div>
          );
        }
        return (
          <div key={p.id} className="pj-photo" style={{ '--r': `${r}deg` }}>
            <span className="pj-tape" />
            <div className="pj-ph" role="img" aria-label={p.caption} style={{ backgroundImage: `url("${String(p.url).replace(/"/g, '%22')}")` }} />
            <small>{p.caption}</small>
          </div>
        );
      })}
    </div>
  );
}

// "while you were out" (D12): the APP's summary of a real gap, so it is
// PRINTED on a slip taped to the top of Today, never in his hand.
const firstOf = (ownerName) => String(ownerName || 'you').trim().split(/\s+/)[0] || 'you';

function awayLine(w) {
  if (!w) return '';
  const parts = [];
  if (w.newMsgs > 0) parts.push(`${w.newMsgs} new message${w.newMsgs === 1 ? '' : 's'}`);
  if (w.newWindow) parts.push(`a new window: ${w.newWindow}`);
  if (w.newKeepsake) parts.push('something new in Keepsakes');
  return parts.length ? `While you were out: ${parts.join(' · ')}.` : '';
}

// Which Today page is showing, and its neighbours (only days with entries get pages).
function todayModel(journal, view, now) {
  const todayKey = localDayKey(now);
  const pastKeys = journal.days.map((d) => d.key).filter((k) => k < todayKey);
  const showing = view.day || todayKey;
  const isToday = showing === todayKey;
  const day = journal.days.find((d) => d.key === showing) || { key: showing, entries: [], taped: [] };
  const olderKey = [...pastKeys].reverse().find((k) => k < showing) || null;
  const newerKey = isToday ? null : (journal.days.map((d) => d.key).find((k) => k > showing && k < todayKey) || todayKey);
  return { todayKey, showing, isToday, day, olderKey, newerKey };
}

function TodayPage({ journal, view, writing, createdAt, now, whileAway, onDismissAway, name, ownerName }) {
  const { showing, isToday, day } = todayModel(journal, view, now);
  const dayN = createdAt ? dayNumber(createdAt, isToday ? now : new Date(`${showing}T12:00:00`)) : null;
  // the newest thought sits on the "on my mind" sticky; the page lists the ones before it
  const entries = isToday && journal.onMind && !writing
    ? day.entries.filter((e) => e.id !== journal.onMind.id) : day.entries;
  const away = isToday ? awayLine(whileAway) : '';
  return (
    <>
      {away ? (
        <div className="pj-slip" role="note">
          {away}
          <button type="button" className="pj-x" aria-label="Dismiss" onClick={onDismissAway}>✕</button>
        </div>
      ) : null}
      <div className="pj-date">{pageLabel(showing)}</div>
      {dayN ? <div className="pj-day">day {dayN}</div> : null}
      {isToday && (journal.onMind || writing) ? (
        <div className="pj-sticky">
          <div className="pj-skh">on my mind</div>
          {writing
            ? <div className="pj-line pj-inking" aria-label="writing" />
            : <div className="pj-line">{journal.onMind.text}</div>}
        </div>
      ) : null}
      {entries.map((e) => (
        <div className="pj-entry" key={e.id}>
          <span className="pj-t" title={e.timeFull || undefined}>{e.time}</span>
          <div className="pj-line">{e.text}</div>
        </div>
      ))}
      <Taped items={day.taped} name={name} ownerName={ownerName} pageDay={showing} />
    </>
  );
}

function TodayFlip({ journal, view, setView, now }) {
  const { todayKey, isToday, olderKey, newerKey } = todayModel(journal, view, now);
  return (
    <div className="pj-flip">
      {olderKey
        ? <button type="button" className="pj-link" onClick={() => setView({ tab: 'today', day: olderKey })}>‹ {pageLabel(olderKey)}</button>
        : <span className="pj-hand" style={{ fontSize: 15 }}>the first page</span>}
      {!isToday
        ? <button type="button" className="pj-link" onClick={() => setView({ tab: 'today', day: newerKey === todayKey ? null : newerKey })}>{newerKey === todayKey ? 'today ›' : `${pageLabel(newerKey)} ›`}</button>
        : <span />}
    </div>
  );
}

// 📋 W5: the Tasks page. Owner only (the page wires it for the owner alone).
function TasksPage({ tasks, name, ownerName, tz, writing, now, onTask, onCancel, onReopen = null, draft, setDraft, when, setWhen, pen, setPen, busy, note, sendTask, shared = false }) {
  const page = taskPage(tasks);
  const chips = taskChips(now, tz);
  const nowMs = now.getTime();
  // his next turn takes ONE due task, the soonest: only that one can be "working on it"
  const nextUid = (page.open.find((t) => { const d = Date.parse(t.due_at || ''); return Number.isFinite(d) && d <= nowMs; }) || {}).uid || null;
  const row = (t, closed) => {
    const hand = taskHandLine(t, { writing, nowMs, next: t.uid === nextUid });
    return (
    <div key={t.uid} className={`pj-task${closed ? ' pj-closed-task' : ''}`} data-task-state={t.state}>
      <span className="pj-box" aria-hidden="true">{t.state === 'done' ? '✓' : ''}</span>
      <div className="pj-task-body">
        <div className="pj-own">{t.text}</div>
        {hand
          ? <div className="pj-line pj-onit" data-task-hand="">{hand}</div>
          : <div className="pj-printed">{taskStatus(t, { ownerName, tz, writing, nowMs, next: t.uid === nextUid })}</div>}
        {t.state === 'done' && t.result ? <div className="pj-line pj-result">{handLine(ownerify(t.result, ownerName), 260)}</div> : null}
        {closed && t.state === 'done' && onReopen ? (
          <div className="pj-acts"><button type="button" className="pj-act" data-task-undo="" aria-label={`Undo the tick: ${t.text}`} onClick={() => onReopen(t.uid)}>undo</button></div>
        ) : null}
      </div>
      {!closed && onCancel ? <button type="button" className="pj-forget" aria-label={`Take it off the list: ${t.text}`} onClick={() => onCancel(t.uid)}>✕</button> : null}
    </div>
    );
  };
  return (
    <>
      <div className="pj-head">my list</div>
      {page.open.map((t) => row(t, false))}
      {!page.open.length && !page.closed.length && !pen ? <div className="pj-printed">Ask {name} for a poem, a letter, a plan, or a reminder.</div> : null}
      {onTask && !pen && !page.full ? (
        <div className="pj-acts"><button type="button" className="pj-act" onClick={() => setPen(true)}>+ ask {name} to do something</button></div>
      ) : null}
      {page.full && !pen ? <div className="pj-printed">Three at a time. One finishes, another fits.</div> : null}
      {page.full && pen ? <div className="pj-printed">Three at a time. One finishes, another fits.</div> : null}
      {pen && onTask && !page.full ? (
        <div className="pj-penbox">
          <textarea className="pj-pen" aria-label={`Something for ${name} to do`} placeholder="a poem about the harvest moon" maxLength={280} rows={3}
            value={draft} onChange={(e) => setDraft(e.target.value)} />
          <button type="button" className="pj-x" aria-label="Put it away" onClick={() => { setPen(false); setDraft(''); }}>✕</button>
          <div className="pj-when" role="group" aria-label="When">
            {chips.map((c) => (
              <button key={c.key} type="button" className="pj-pick" aria-pressed={when === c.key} onClick={() => setWhen(c.key)}>{c.label}</button>
            ))}
          </div>
          <div className="pj-printed">{note || (shared
            ? `now, when you leave, or later. it lands here and in the chat, and everyone in this room sees what ${name} writes.`
            : 'now, when you leave, or later. it lands here and in the chat.')}</div>
          <div className="pj-acts">
            <button type="button" className="pj-act" disabled={!draft.trim() || !when || busy} onClick={() => sendTask(chips.find((c) => c.key === when))}>{busy ? 'adding it…' : 'put it on the list'}</button>
          </div>
        </div>
      ) : null}
      {page.closed.length ? <div className="pj-head">done</div> : null}
      {page.closed.map((t) => row(t, true))}
      <div className="pj-printed">only you can see this page.</div>
    </>
  );
}

export default function PortedJournal({
  residentName = null,
  ownerName = null,
  isOwner = false,
  createdAt = null,
  writing = false,
  cover = 'light',
  journal = { onMind: null, days: [], keepsakes: [], moments: [] },
  onDraw = null,
  whileAway = null,   // { newMsgs, newKeepsake, newWindow } on a real-gap return
  fill = null,        // 'column' (desktop: the column's height) | 'screen' (phones) | null
  pronouns = null,    // D11: the owner's own ('she/her' | 'he/him' | 'they/them'), never guessed
  onPronouns = null,  // (value|null) => void: the in-room change; absent = no control
  facts = null,       // D10: [{ id, text, gone? }] in a PRIVATE room, else null (nothing drawn)
  onForget = null,    // (id) => void
  now = null,
  openToken = 0,      // 📓 W2d: a new value opens today's page (the room's notebook hotspot)
  fit = false,        // 🏡 W2d: with fill="column", the book follows a short column (no 380px floor)
  notes = null,       // ✉️ W2c: the owner's notes [{ uid, text, at, state, reply, reply_at }] (owner POST only)
  onNote = null,      // ✉️ W2c: (text) => Promise; absent = no "leave a note" door
  // ── 📋 W5 ──
  tasks = null,       // the owner's list (owner POST only) or null
  onTask = null,      // (add body) => Promise; absent = no Tasks tab at all
  onCancelTask = null, // (uid) => Promise
  taskToken = null,   // { n, text }: `/task words` opened this page (a new n = open it)
  taskShared = false, // a SHARED room: the pen says everyone sees the finished thing
  tz = null,          // the owner's zone (the chips' times)
  // ── 🧠📖 wave 2 C2 ──
  memories = null,    // usePortedMemories' controller (owner POST only) or null = nothing drawn
  onForgetMemory = null, // (id) => Promise: ✕ makes him forget one memory
  // ── wave 2b ──
  onReopenTask = null, // (uid) => Promise: "undo" a ✓ (owner); absent = no undo
  favorites = null,    // the status ported block's `favorites` (newest first): his weekly pick, in Keepsakes
}) {
  const [view, setView] = useState({ tab: 'today', day: null });
  // ✉️ the pen (W2c): open/closed, the draft and the answer live here, above
  // the leaf, so a tab switch (a fresh leaf) keeps a half-written note
  const [pen, setPen] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [penNote, setPenNote] = useState(null);
  const [justSent, setJustSent] = useState(false);
  useEffect(() => { setJustSent(false); }, [notes]);
  useEffect(() => { ensureHandFont(); }, []);
  // 📓 W2d: the 📓 on its spot in the painted room opens THIS book at today's
  // page: back to Today, the top of the page, and the Today tab takes focus
  // (the person's eye follows the focus ring across the page).
  const rootRef = useRef(null);
  const [called, setCalled] = useState(false);
  useEffect(() => {
    if (!openToken) return undefined;
    setCalled(true);
    const off = setTimeout(() => setCalled(false), 1400);
    setView({ tab: 'today', day: null });
    const t = setTimeout(() => {
      const el = rootRef.current;
      if (!el) return;
      const leaf = el.querySelector('.pj-leaf');
      if (leaf) leaf.scrollTop = 0;
      const tab = el.querySelector('.pj-tab');
      if (tab) { try { tab.focus({ preventScroll: true }); } catch (e) { tab.focus(); } }
    }, 0);
    return () => { clearTimeout(t); clearTimeout(off); };
  }, [openToken]);
  const [awayDismissed, setAwayDismissed] = useState(false);
  // 📋 the task pen lives here too (a tab switch keeps a half-written ask)
  const [taskPen, setTaskPen] = useState(false);
  const [taskDraft, setTaskDraft] = useState('');
  const [taskWhen, setTaskWhen] = useState(null);
  const [taskBusy, setTaskBusy] = useState(false);
  const [taskNote, setTaskNote] = useState(null);
  useEffect(() => {
    if (!taskToken || !taskToken.n || !onTask) return;
    // a remount (a breakpoint crossed) must never replay an old /task: only a fresh token opens the page
    if (taskToken.at && Date.now() - taskToken.at > 3000) return;
    setView({ tab: 'tasks', day: null });
    if (taskToken.pen !== false) setTaskPen(true);
    if (taskToken.text) setTaskDraft(String(taskToken.text).slice(0, 280));
    setCalled(true);
    const off = setTimeout(() => setCalled(false), 1400);
    return () => clearTimeout(off);
  }, [taskToken && taskToken.n]); // eslint-disable-line react-hooks/exhaustive-deps
  const TASK_WHY = { full: 'Three at a time. One finishes, another fits.', too_far: 'Within a day, please.', too_soon: 'A little later than that.', empty: 'Write what you want first.' };
  const sendTask = async (picked) => {
    const text = taskDraft.trim();
    // the chip's time is read at the moment of sending (the page's clock ticks
    // once a minute: a stale "at 9" could land in the past)
    const chip = picked ? (taskChips(new Date(), tz).find((c) => c.key === picked.key) || picked) : null;
    if (!text || !chip || taskBusy || !onTask) return;
    setTaskBusy(true);
    setTaskNote(null);
    try {
      const res = await onTask({ text, when: chip.when, ...(chip.at ? { at: chip.at } : {}) });
      const out = res && res.owner_task ? res.owner_task : res;
      if (out && out.ok === false) setTaskNote(TASK_WHY[out.reason] || "That didn't go through. Try again in a minute.");
      else { setTaskDraft(''); setTaskWhen(null); setTaskPen(false); }
    } catch (e) {
      setTaskNote("That didn't go through. Try again in a minute.");
    } finally {
      setTaskBusy(false);
    }
  };
  const clock = useMemo(() => now || new Date(), [now]);
  const name = residentName || 'Your resident';
  const book = useMemo(() => journalWithNotes(journal, notes), [journal, notes]);
  const favLines = useMemo(() => favoriteLines(favorites, { ownerName, now: clock, tz }), [favorites, ownerName, clock, tz]);
  // one note at a time: the door comes back once the last one was read
  const noteDoor = !!onNote && !pen && !justSent && !noteWaiting(notes);
  const tapeNote = async () => {
    const text = draft.trim();
    if (!text || sending || !onNote) return;
    setSending(true);
    setPenNote(null);
    try {
      const res = await onNote(text);
      const out = res && res.owner_note ? res.owner_note : res;
      if (out && out.ok === false) {
        setPenNote(out.reason === 'waiting' ? `${name} still has your last note.` : "That didn't go through. Try again in a minute.");
      } else {
        setDraft('');
        setPen(false);
        setJustSent(true);
      }
    } catch (e) {
      setPenNote("That didn't go through. Try again in a minute.");
    } finally {
      setSending(false);
    }
  };
  const c = COVERS[cover] || COVERS.light;
  const root = `pj${fill === 'column' ? ` pj-col${fit ? ' pj-fit' : ''}` : fill === 'screen' ? ' pj-scr' : ''}${called ? ' pj-called' : ''}`;

  if (!isOwner) {
    // 🔒 Owner-only (§2 privacy): guests in a shared port see a closed book.
    return (
      <div className={root}>
        <style>{CSS}</style>
        <div className="pj-closed" style={{ background: `linear-gradient(90deg, rgba(0,0,0,.35) 0 9%, ${c.cv} 9%)` }}>
          <span className="pj-band" />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <b>{name}&rsquo;s journal</b>
            <span className="pj-lock">🔒 private</span>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [['today', 'Today'], ['keepsakes', 'Keepsakes'], ...(onTask ? [['tasks', 'Tasks']] : []), ['you', ownerTabLabel(ownerName)]];
  return (
    <div className={root} ref={rootRef} aria-label={`${name}'s journal`}>
      <style>{CSS}</style>
      <div className="pj-cover" style={{ '--cv': c.cv, '--corner': c.corner }}>
        <span className="pj-edges" />
        <span className="pj-ribbon" />
        <div className="pj-book">
          <div className="pj-tabs" role="group" aria-label="Journal pages">
            {tabs.map(([k, label]) => (
              <button key={k} type="button" className="pj-tab" aria-pressed={view.tab === k}
                onClick={() => setView({ tab: k, day: null })}>{label}</button>
            ))}
          </div>
          <div className="pj-page">
            <div className="pj-leaf" key={`${view.tab}:${view.day || ''}`}>
            {view.tab === 'today' && (
              <TodayPage journal={book} view={view} writing={writing} createdAt={createdAt} now={clock}
                whileAway={awayDismissed ? null : whileAway} onDismissAway={() => setAwayDismissed(true)}
                name={name} ownerName={ownerName} />
            )}
            {view.tab === 'keepsakes' && (
              <>
                <div className="pj-head">things we made</div>
                {favLines.map((f) => <div className="pj-line pj-fav" key={f.id} data-favorite={f.kind || ''}>{f.text}</div>)}
                {onDraw || noteDoor ? (
                  <div className="pj-acts">
                    {onDraw ? <button type="button" className="pj-act" onClick={onDraw}>✏️ draw something</button> : null}
                    {noteDoor ? <button type="button" className="pj-act" onClick={() => { setPen(true); setPenNote(null); }}>✉️ leave a note</button> : null}
                  </div>
                ) : null}
                {pen && onNote ? (
                  <div className="pj-penbox">
                    <textarea
                      className="pj-pen"
                      aria-label={`A note for ${name}`}
                      placeholder={`Dear ${name},`}
                      maxLength={500}
                      rows={4}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                    />
                    <button type="button" className="pj-x" aria-label="Put the note away" onClick={() => { setPen(false); setDraft(''); setPenNote(null); }}>✕</button>
                    <div className="pj-printed">{penNote || `${name} reads it next time, and may write back.`}</div>
                    <div className="pj-acts">
                      <button type="button" className="pj-act" disabled={!draft.trim() || sending} onClick={tapeNote}>{sending ? 'taping it in…' : 'tape it in'}</button>
                    </div>
                  </div>
                ) : null}
                {book.keepsakes.length
                  ? <Taped items={keepsakePage(book.keepsakes, 12)} name={name} ownerName={ownerName} pageDay={localDayKey(clock)} />
                  : <div className="pj-printed">What you and {name} make lands here.</div>}
              </>
            )}
            {view.tab === 'tasks' && onTask && (
              <TasksPage tasks={tasks} name={name} ownerName={ownerName} tz={tz} writing={writing} now={clock}
                onTask={onTask} onCancel={onCancelTask} onReopen={onReopenTask} draft={taskDraft} setDraft={setTaskDraft} when={taskWhen} setWhen={setTaskWhen}
                pen={taskPen} setPen={(v) => { setTaskPen(v); setTaskNote(null); }} busy={taskBusy} note={taskNote} sendTask={sendTask} shared={taskShared} />
            )}
            {view.tab === 'you' && (
              <>
                <div className="pj-date">{String(ownerName || 'you').trim().split(/\s+/)[0] || 'you'}</div>
                {onPronouns ? (
                  <div className="pj-pron" role="group" aria-label="Your pronouns">
                    <span>your pronouns:</span>
                    {PRONOUN_KEYS.map((k) => (
                      <button key={k} type="button" className="pj-pick" aria-pressed={pronouns === k} onClick={() => onPronouns(k)}>{k}</button>
                    ))}
                    <button type="button" className="pj-pick" aria-pressed={!pronouns} onClick={() => onPronouns(null)}>just my name</button>
                  </div>
                ) : null}
                <div className="pj-head">special moments</div>
                {journal.moments.length
                  ? journal.moments.map((m) => (
                    <div className="pj-star" key={m.id}>⭐ {m.text}{m.when ? <span className="pj-when">{m.when}</span> : null}</div>
                  ))
                  : <div className="pj-printed">The moments {name} stars land here.</div>}
                {/* 🧠📖 wave 2 C2: what he remembers of this room, with ✕ (owner POST only; draws nothing while off) */}
                {memories ? <PortedMemories memories={memories} onForget={onForgetMemory} name={name} /> : null}
                {Array.isArray(facts) && facts.length ? (
                  <>
                    <div className="pj-head">what i know about {firstOf(ownerName)}</div>
                    {facts.map((f) => (
                      <div className="pj-fact" key={f.id}>
                        <div className={`pj-line${f.gone ? ' pj-gone' : ''}`}>{ownerify(f.text, ownerName)}</div>
                        {onForget && !f.gone ? (
                          <button type="button" className="pj-forget" aria-label={`Forget: ${f.text}`} onClick={() => onForget(f.id)}>✕</button>
                        ) : null}
                      </div>
                    ))}
                    <div className="pj-printed">✕ makes {name} forget it.</div>
                  </>
                ) : null}
                <div className="pj-printed">only you can see this page.</div>
              </>
            )}
            </div>
            {view.tab === 'today' ? <TodayFlip journal={book} view={view} setView={setView} now={clock} /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
